/**
 * Helper funkcije i konstante za rad sa limitima
 * Centralizovane definicije tipova i helper funkcije
 */

import { getDefaultLimitConfig } from '@/models/limitConfiguration';

// ============================================================================
// DEFINICIJE TIPOVA
// ============================================================================

export const LIMIT_TYPES = [
    {
        value: "order",
        i18nKey: "order_limit",
        descKey: "order_limit_desc",
        icon: "mdi-clipboard-list",
        color: "blue",
        hasSlot: true,
    },
    {
        value: "voucher",
        i18nKey: "voucher_limit",
        descKey: "voucher_limit_desc",
        icon: "mdi-ticket-percent",
        color: "green",
        hasSlot: true,
    },
    {
        value: "credit",
        i18nKey: "credit_limit",
        descKey: "credit_limit_desc",
        icon: "mdi-credit-card",
        color: "orange",
        hasSlot: false,
    },
    {
        value: "money",
        i18nKey: "money_limit",
        descKey: "money_limit_desc",
        icon: "mdi-cash",
        color: "purple",
        hasSlot: false,
    },
    {
        value: "unlimited",
        i18nKey: "no_limit",
        descKey: "no_limit_desc",
        icon: "mdi-infinity",
        color: "grey",
        hasSlot: false,
    },
];

export const TIME_LIMIT_TYPES = [
    {
        value: "no_limit",
        i18nKey: "no_time_limit",
        descKey: "no_time_limit_desc",
        icon: "mdi-infinity",
        color: "grey",
        hasSlot: false,
    },
    {
        value: "day",
        i18nKey: "daily_limit",
        descKey: "daily_limit_desc",
        icon: "mdi-calendar-today",
        color: "blue",
        hasSlot: true,
    },
    {
        value: "shift",
        i18nKey: "shift_limit",
        descKey: "shift_limit_desc",
        icon: "mdi-clock-outline",
        color: "orange",
        hasSlot: true,
    },
    {
        value: "week",
        i18nKey: "weekly_limit",
        descKey: "weekly_limit_desc",
        icon: "mdi-calendar-week",
        color: "green",
        hasSlot: true,
    },
    {
        value: "month",
        i18nKey: "monthly_limit",
        descKey: "monthly_limit_desc",
        icon: "mdi-calendar-month",
        color: "purple",
        hasSlot: true,
    },
    {
        value: "custom",
        i18nKey: "custom_period",
        descKey: "custom_period_desc",
        icon: "mdi-calendar-range",
        color: "teal",
        hasSlot: true,
    },
];

export const WHO_PAYS_TYPES = [
    {
        value: "company_only",
        i18nKey: "company_100",
        descKey: "company_pays_all_costs",
        icon: "mdi-office-building",
        color: "blue",
        hasSlot: false,
    },
    {
        value: "user_only",
        i18nKey: "user_100",
        descKey: "user_pays_all_costs",
        icon: "mdi-account",
        color: "purple",
        hasSlot: false,
    },
    {
        value: "split",
        i18nKey: "combined_payment",
        descKey: "costs_split_between_company_and_user",
        icon: "mdi-scale-balance",
        color: "orange",
        hasSlot: true,
    },
    {
        value: "company_payroll_deduct",
        i18nKey: "company_pays_deducts_from_salary",
        descKey: "company_pays_restaurant_deducts_from_salary",
        icon: "mdi-cash-minus",
        color: "green",
        hasSlot: true,
    },
];

export const PACKAGES_OPTIONS = [
    { value: "all", i18nKey: "limit_packages_all" },
    { value: "only", i18nKey: "limit_packages_only" },
    { value: "none", i18nKey: "limit_packages_none" },
];

export function getPackagesLabelKey(value) {
    const found = PACKAGES_OPTIONS.find(o => o.value === value);
    return (found || PACKAGES_OPTIONS[0]).i18nKey;
}

export const WHAT_CAN_ORDER_TYPES = [
    {
        value: "full_meal",
        i18nKey: "complete_meal_option",
        descKey: "user_can_order_complete_meal",
        icon: "mdi-food",
        color: "green",
        hasSlot: false,
    },
    {
        value: "group_selection",
        i18nKey: "meals_from_group",
        descKey: "user_can_order_meals_from_group",
        icon: "mdi-silverware-fork-knife",
        color: "purple",
        hasSlot: true,
    },
    {
        value: "daily_menu",
        i18nKey: "meals_from_daily_menu",
        descKey: "user_can_order_from_daily_menu",
        icon: "mdi-calendar-today",
        color: "orange",
        hasSlot: false,
    },
];

export const OVER_LIMIT_TYPES = [
    {
        value: true,
        i18nKey: "block_orders",
        descKey: "prevent_orders_exceeding_limit",
        icon: "mdi-cancel",
        color: "red",
        hasSlot: false,
    },
    {
        value: false,
        i18nKey: "allow_orders_over_limit",
        descKey: "allow_orders_exceeding_limit",
        icon: "mdi-check-circle",
        color: "green",
        hasSlot: false,
    },
];

// ============================================================================
// HELPER FUNKCIJE
// ============================================================================

/**
 * Rekurzivno primenjuje default vrednosti iz modela na objekat iz API-ja
 * @param {Object} target - Objekat iz API-ja
 * @param {Object} defaults - Default model sa svim poljima
 * @returns {Object} Objekat sa primenjenim default vrednostima
 */
export function applyDefaults(target = {}, defaults = {}) {
    const result = { ...target };

    Object.keys(defaults).forEach((key) => {
        const defaultValue = defaults[key];

        if (typeof defaultValue === "object" && defaultValue !== null && !Array.isArray(defaultValue)) {
            // Rekurzivno primeni defaults na ugneždene objekte
            result[key] = applyDefaults(result[key] || {}, defaultValue);
        } else {
            // Primeni default vrednost samo ako ne postoji
            result[key] = result[key] ?? defaultValue;
        }
    });

    return result;
}

/**
 * Normalizuje limit podatke iz API-ja primenjujući defaults iz getDefaultLimitConfig()
 * @param {Object} limit - Limit objekat iz API-ja
 * @returns {Object} Normalizovan limit objekat
 */
export function normalizeLimitData(limit) {
    if (!limit.params) {
        limit.params = {};
    }

    // Uzmi defaults iz centralne funkcije
    const defaults = getDefaultLimitConfig();

    // Primeni defaults za svaki deo params objekta
    limit.params.limit_definition = applyDefaults(limit.params.limit_definition, defaults.limit_definition);
    limit.params.timing = applyDefaults(limit.params.timing, defaults.timing);
    limit.params.payment_logic = applyDefaults(limit.params.payment_logic, defaults.payment_logic);
    limit.params.rollover_policy = applyDefaults(limit.params.rollover_policy, defaults.rollover_policy);
    limit.params.ordering_rules = applyDefaults(limit.params.ordering_rules, defaults.ordering_rules);

    return limit;
}

/**
 * Vraća i18n ključ za tip limita
 * @param {string} type - Tip limita (order, voucher, credit, money, unlimited)
 * @returns {string} i18n ključ za tip limita
 */
export function getLimitTypeLabelKey(type) {
    const found = LIMIT_TYPES.find(t => t.value === type);
    return found?.i18nKey || type || "";
}

/**
 * Vraća i18n ključ za vremenski limit
 * @param {string} type - Tip vremenskog limita (no_limit, day, shift, week, month, year, custom)
 * @returns {string} i18n ključ za vremenski limit
 */
export function getTimeLimitLabelKey(type) {
    // Normalizuj alternativne nazive
    const normalizedType = type === 'daily' ? 'day' : type === 'weekly' ? 'week' : type === 'monthly' ? 'month' : type === 'yearly' ? 'year' : type;
    const found = TIME_LIMIT_TYPES.find(t => t.value === normalizedType);
    return found?.i18nKey || type || "";
}

/**
 * Vraća i18n ključ za način plaćanja
 * @param {string} type - Način plaćanja (company_only, user_only, split, company_payroll_deduct)
 * @returns {string} i18n ključ za način plaćanja
 */
export function getWhoPaysLabelKey(type) {
    const found = WHO_PAYS_TYPES.find(t => t.value === type);
    return found?.i18nKey || type || "";
}

/**
 * Vraća i18n ključ za tip narudžbine
 * @param {string} type - Tip narudžbine (full_meal, group_selection, daily_menu)
 * @returns {string} i18n ključ za tip narudžbine
 */
export function getWhatCanOrderLabelKey(type) {
    const found = WHAT_CAN_ORDER_TYPES.find(t => t.value === type);
    return found?.i18nKey || type || "";
}

/**
 * Vraća i18n ključ za prekoračenje limita
 * @param {boolean} blockOrder - Da li se blokira narudžbina
 * @returns {string} i18n ključ za prekoračenje limita
 */
export function getOverLimitLabelKey(blockOrder) {
    const found = OVER_LIMIT_TYPES.find(t => t.value === blockOrder);
    return found?.i18nKey || "";
}

/**
 * Vraća i18n ključ za rollover period
 * @param {string} type - Rollover period (day, week, month, year)
 * @returns {string} i18n ključ za rollover period
 */
export function getRolloverLabelKey(type) {
    const keys = {
        day: "next_day",
        next_day: "next_day",
        week: "next_week",
        next_week: "next_week",
        month: "next_month",
        next_month: "next_month",
        year: "next_year",
        next_year: "next_year",
        no_limit: "no_limit",
    };
    return keys[type] || type || "";
}

// ============================================================================
// SCOPE / PRIORITY HELPERS
// ============================================================================

/**
 * Vraća scope tip limita: restaurant_base | restaurant_shared | company_only | unknown
 */
export function getLimitScopeType(item) {
    const hasCompanyId = item.company_id !== null && item.company_id !== undefined;
    const hasRestaurantId = item.restaurant_id !== null && item.restaurant_id !== undefined;

    if (!hasCompanyId) return "restaurant_base";
    if (hasCompanyId && hasRestaurantId) return "restaurant_shared";
    if (hasCompanyId && !hasRestaurantId) return "company_only";
    return "unknown";
}

/**
 * Vraća numerički prioritet za sortiranje: restaurant_base=0, restaurant_shared=1, company_only=2
 */
export function getLimitPriority(item) {
    const scopeType = getLimitScopeType(item);
    if (scopeType === "restaurant_base") return 0;
    if (scopeType === "restaurant_shared") return 1;
    if (scopeType === "company_only") return 2;
    return 3;
}

/**
 * Komparator za sort — po prioritetu, onda po imenu (sr locale)
 */
export function sortLimitsByPriority(a, b) {
    const diff = getLimitPriority(a) - getLimitPriority(b);
    if (diff !== 0) return diff;
    return String(a.name || "").localeCompare(String(b.name || ""), "sr");
}
